import { type Settings } from "./settings";
import { type Dispatcher } from "./studio-state";
import { flushSync } from "react-dom";
import {
  loadAudioNoiseSuppressionMode,
  saveAudioNoiseSuppressionMode,
} from "./audio/noise-suppression/prefs";
import {
  createNoiseSuppressedRuntime,
  disposeNoiseSuppressionRuntime,
} from "./audio/noise-suppression/runtime";
import { onMobileDevice } from "./util";


const normalizeConstrainDomString = (value?: ConstrainDOMString): string[] => {
  if (value == null) {
    return [];
  }

  if (typeof value === "string") {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === "string");
  }

  const exact = value.exact;
  const ideal = value.ideal;
  const out: string[] = [];

  if (typeof exact === "string") {
    out.push(exact);
  } else if (Array.isArray(exact)) {
    out.push(...exact.filter((v): v is string => typeof v === "string"));
  }

  if (typeof ideal === "string") {
    out.push(ideal);
  } else if (Array.isArray(ideal)) {
    out.push(...ideal.filter((v): v is string => typeof v === "string"));
  }

  return out;
};

const resolveAudioConstraint = async (
  deviceId?: ConstrainDOMString,
): Promise<true | { deviceId: ConstrainDOMString }> => {
  if (!deviceId) {
    return true;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const availableIds = new Set(devices.filter(d => d.kind === "audioinput").map(d => d.deviceId));
    const preferredIds = normalizeConstrainDomString(deviceId);

    // If none of the preferred IDs exist anymore (e.g. unplugged USB mic),
    // fall back to browser default microphone selection.
    if (preferredIds.length > 0 && !preferredIds.some(id => availableIds.has(id))) {
      return true;
    }
  } catch {
    // If enumeration fails, keep the original constraint and let gUM decide.
  }

  return { deviceId };
};

const isExactDeviceConstraint = (value?: ConstrainDOMString): boolean => {
  if (!value || typeof value === "string" || Array.isArray(value)) {
    return false;
  }

  return value.exact != null;
};

const getUserMediaWithTimeout = async (
  constraints: MediaStreamConstraints,
  timeoutMs: number,
): Promise<MediaStream> => {
  let timeoutId: number | null = null;
  let timedOut = false;

  const gUMPromise = navigator.mediaDevices.getUserMedia(constraints);

  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = window.setTimeout(() => {
      timedOut = true;
      reject(new Error("getUserMedia timed out"));
    }, timeoutMs);
  });

  try {
    const stream = await Promise.race([gUMPromise, timeoutPromise]);
    return stream;
  } catch (err) {
    if (timedOut) {
      // If the browser resolves late after timeout, close tracks to avoid leaks.
      void gUMPromise.then(stream => {
        try {
          stream.getTracks().forEach(track => track.stop());
        } catch {
          // ignore
        }
      }).catch(() => {
        // ignore
      });
    }
    throw err;
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
};


const mergeHeightConstraint = (
  maxHeight: number | undefined,
  videoConstraints: MediaTrackConstraints,
  fallbackIdeal?: number,
) => {
  const maxField = maxHeight && { max: maxHeight };
  const constraintIdeal = typeof videoConstraints?.height === "number"
    ? videoConstraints.height
    : videoConstraints.height?.ideal;
  const ideal = constraintIdeal ?? fallbackIdeal;
  const idealField = ideal && (maxHeight ? { ideal: Math.min(ideal, maxHeight) } : { ideal });

  return { height: { ...maxField, ...idealField } };
};

export async function startAudioCapture(
  dispatch: Dispatcher,
  deviceId?: ConstrainDOMString,
): Promise<MediaStream | null> {
  const share = async (stream: MediaStream) => {
    stream.getTracks().forEach(track => {
      track.onended = () => {
        dispatch({ type: "AUDIO_UNEXPECTED_END" });
      };
    });

    // Read the device ID from the raw capture track now: once noise
    // suppression wraps it, the shared track becomes a synthetic
    // destination-node track whose getSettings() no longer reports a deviceId.
    const rawDeviceId = stream.getAudioTracks()[0]?.getSettings()?.deviceId ?? null;

    let streamToShare = stream;
    const preferredMode = loadAudioNoiseSuppressionMode();
    dispatch({ type: "SET_AUDIO_NOISE_SUPPRESSION_MODE", mode: preferredMode });

    if (preferredMode !== "none") {
      dispatch({ type: "BEGIN_AUDIO_NOISE_SUPPRESSION_LOADING" });
      try {
        const runtime = await createNoiseSuppressedRuntime(stream, preferredMode, {
          onRuntimeFailure: () => {
            saveAudioNoiseSuppressionMode("none");
            dispatch({ type: "SET_AUDIO_NOISE_SUPPRESSION_MODE", mode: "none" });
            dispatch({ type: "SET_AUDIO_NOISE_SUPPRESSION_AVAILABLE", available: false });
          },
        });
        if (runtime) {
          streamToShare = runtime.processedStream;
        }
      } catch (error) {
        console.warn("Noise suppression unavailable, falling back to bypass", error);
        saveAudioNoiseSuppressionMode("none");
        dispatch({ type: "SET_AUDIO_NOISE_SUPPRESSION_AVAILABLE", available: false });
        dispatch({ type: "SET_AUDIO_NOISE_SUPPRESSION_MODE", mode: "none" });
      } finally {
        dispatch({ type: "END_AUDIO_NOISE_SUPPRESSION_LOADING" });
      }
    }

    streamToShare.getTracks().forEach(track => {
      track.onended = () => {
        dispatch({ type: "AUDIO_UNEXPECTED_END" });
      };
    });

    dispatch({ type: "SHARE_AUDIO", stream: streamToShare, deviceId: rawDeviceId });
    return streamToShare;
  };

  try {
    const audioConstraint = await resolveAudioConstraint(deviceId);
    const stream = deviceId
      ? await getUserMediaWithTimeout({ audio: audioConstraint, video: false }, 5000)
      : await navigator.mediaDevices.getUserMedia({
        audio: audioConstraint,
        video: false,
      });
    return await share(stream);
  } catch (err) {
    // If opening a preferred mic fails, retry once with default input.
    // But if the user explicitly chose a concrete device (`exact`), do not
    // silently switch to default; keep user intent intact.
    if (deviceId && !isExactDeviceConstraint(deviceId)) {
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        return await share(fallbackStream);
      } catch (fallbackErr) {
        // TODO: there several types of exceptions; certainly we should differentiate here one day
        console.error("Error: ", fallbackErr);
      }
    } else {
      // TODO: there several types of exceptions; certainly we should differentiate here one day
      console.error("Error: ", err);
    }

    dispatch({ type: "BLOCK_AUDIO" });
    return null;
  }
}

export async function startDisplayCapture(
  dispatch: Dispatcher,
  settings: Settings,
  videoConstraints: MediaTrackConstraints = {},
) {
  const maxFps = settings.display?.maxFps
    ? { frameRate: { max: settings.display.maxFps } }
    : {};
  const height = mergeHeightConstraint(settings.display?.maxHeight, videoConstraints);

  const constraints = {
    video: {
      cursor: "always",
      ...maxFps,
      ...videoConstraints,
      ...height,
    },
    audio: true,
  };

  try {
    const stream = await navigator.mediaDevices.getDisplayMedia(constraints);
    stream.getTracks().forEach(track => {
      track.onended = () => {
        dispatch({ type: "DISPLAY_UNEXPECTED_END" });
      };
    });

    const vTrack = stream.getVideoTracks()[0];
    if (vTrack) {
      const s = vTrack.getSettings() as MediaTrackSettings & { displaySurface?: string };
      const res = (s.width && s.height) ? `${s.width}\u00d7${s.height}` : "unknown";
      const fps = s.frameRate != null ? `${Math.round(s.frameRate)}fps` : "unknown fps";
      const surface = s.displaySurface ?? "unknown";
      const label = vTrack.label || "unknown";
      console.log(
        `[display] stream started: label="${label}", surface=${surface}`
        + `, resolution=${res} @ ${fps}`,
      );
    }

    flushSync(() => dispatch({ type: "SHARE_DISPLAY", stream }));
  } catch (err) {
    // TODO: there 7 types of exceptions; certainly we should differentiate here one day
    console.error("Error: ", err);

    dispatch({ type: "BLOCK_DISPLAY" });
  }
}

export async function startUserCapture(
  dispatch: Dispatcher,
  settings: Settings,
  videoConstraints: MediaTrackConstraints,
) {
  // Mobile cameras often ignore/behave inconsistently with explicit aspect-ratio
  // constraints, so force "auto" behavior by removing the constraint.
  const effectiveVideoConstraints: MediaTrackConstraints = onMobileDevice()
    ? (() => {
      const mobileSafeConstraints = { ...videoConstraints };
      delete mobileSafeConstraints.aspectRatio;
      window.localStorage.setItem("MediasiteWebRecorderCameraAspectRatio", "auto");
      return mobileSafeConstraints;
    })()
    : videoConstraints;

  const maxFps = settings.camera?.maxFps
    ? { frameRate: { max: settings.camera.maxFps } }
    : {};
  const height = mergeHeightConstraint(settings.camera?.maxHeight, effectiveVideoConstraints, 1080);

  const constraints = {
    video: {
      facingMode: "user",
      ...effectiveVideoConstraints,
      ...maxFps,
      ...height,
    },
    audio: false,
  };

  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    stream.getTracks().forEach(track => {
      track.onended = () => {
        dispatch({ type: "USER_UNEXPECTED_END" });
      };
    });
    flushSync(() => dispatch({ type: "SHARE_USER", stream }));
  } catch (err) {
    // TODO: there 7 types of exceptions; certainly we should differentiate here one day
    console.error("Error: ", err);

    dispatch({ type: "BLOCK_USER" });
  }
}

// ----------------------------------------------------------------------------

export function stopCapture(
  { audioStream, displayStream, userStream }: {
    audioStream: MediaStream | null;
    displayStream: MediaStream | null;
    userStream: MediaStream | null;
  },
  dispatch: Dispatcher,
) {
  stopAudioCapture(audioStream, dispatch);
  stopDisplayCapture(displayStream, dispatch);
  stopUserCapture(userStream, dispatch);
}

export function stopAudioCapture(stream: MediaStream | null, dispatch: Dispatcher) {
  void disposeNoiseSuppressionRuntime(stream);
  dispatch({ type: "UNSHARE_AUDIO" });
}

export function stopDisplayCapture(stream: MediaStream | null, dispatch: Dispatcher) {
  stream?.getTracks().forEach(track => track.stop());
  dispatch({ type: "UNSHARE_DISPLAY" });
}

export function stopUserCapture(stream: MediaStream | null, dispatch: Dispatcher) {
  stream?.getTracks().forEach(track => track.stop());
  dispatch({ type: "UNSHARE_USER" });
}
